import { APInvoiceStatus, OriginSyncStatus } from '@erp/shared-types';
import { canMarkInvoicePaid } from '@/components/accounts-payable/mark-paid/markInvoicePaidEligibility';

/** The invoice fields that decide which overflow-menu items exist. */
export type InvoiceOverflowSubject = {
  status: string;
  paymentStatus: string | null | undefined;
  supplierId?: string | null;
  originSyncStatus?: string | null;
  originSystem?: string | null;
};

export type InvoiceOverflowPermissions = {
  canUpdate: boolean;
  canUpdatePayment: boolean;
  canDelete: boolean;
};

export type InvoiceOverflowActions = {
  showReprocess: boolean;
  showMarkPaid: boolean;
  deleteVisible: boolean;
  deleteEnabled: boolean;
};

/**
 * Which overflow-menu actions apply to one invoice. Kept out of the menu
 * component so the status rules can be tested without rendering the menu.
 */
/** True while the menu must stay hidden because the invoice or a permission is still loading. */
export function overflowMenuIsEmpty(items: {
  queueItem: unknown;
  exceptionsItem: unknown;
  showSnooze: boolean;
  showReprocess: boolean;
  showMarkPaid: boolean;
  deleteVisible: boolean;
}): boolean {
  return (
    items.queueItem == null &&
    items.exceptionsItem == null &&
    !items.showSnooze &&
    !items.showReprocess &&
    !items.showMarkPaid &&
    !items.deleteVisible
  );
}

export function invoiceMenuIsWaiting(flags: {
  invoiceLoading: boolean;
  updateLoading: boolean;
  paymentLoading: boolean;
  deleteLoading: boolean;
  invoiceMissing: boolean;
}): boolean {
  return (
    flags.invoiceLoading ||
    flags.updateLoading ||
    flags.paymentLoading ||
    flags.deleteLoading ||
    flags.invoiceMissing
  );
}

export function invoiceOverflowActions(
  invoice: InvoiceOverflowSubject,
  permissions: InvoiceOverflowPermissions,
): InvoiceOverflowActions {
  const draftOrMatched =
    invoice.status === APInvoiceStatus.DRAFT ||
    invoice.status === APInvoiceStatus.MATCHED;
  const famousSynced =
    invoice.originSyncStatus === OriginSyncStatus.Success &&
    invoice.originSystem === 'famous';

  return {
    showReprocess: permissions.canUpdate && draftOrMatched,
    showMarkPaid: permissions.canUpdatePayment && canMarkInvoicePaid(invoice),
    deleteVisible:
      permissions.canDelete &&
      invoice.status !== APInvoiceStatus.FINALIZED &&
      invoice.status !== APInvoiceStatus.VOID,
    deleteEnabled:
      (draftOrMatched || invoice.status === APInvoiceStatus.ACCEPTED) &&
      !famousSynced,
  };
}
